<template>
    <div class="create-client">
        <form-component ref="form" class="new-client-page container" v-if="!clientSaved">
            <div class="row">
                <div class="col">
                    <div class="box">
                        <h4>Cliëntgegevens</h4>
                        <p class="errorMessage" v-if="clientToEdit.connected">Cliënt is op dit moment gekoppeld aan een smartwatch, wijzigen niet mogelijk.</p>
                        <p v-if="clientInitialsErrorMessage" class="errorMessage">{{ clientInitialsErrorMessage }}</p>
                        <input-component
                                label="Anonieme cliëntcode"
                                v-model="clientToEdit.initials"
                                :disabled="clientToEdit.connected">
                        </input-component>
                        <input-component
                                type="number"
                                label="Te laat mogen komen tijd in minuten"
                                v-model="clientToEdit.allowedMinutesToLate"
                                :disabled="clientToEdit.connected">
                        </input-component>
                        <p v-if="clientLocationErrorMessage" class="errorMessage">{{ clientLocationErrorMessage }}</p>
                        <label for="location">Woonlocatie</label>
                        <select
                                id="location"
                                class="form-control"
                                v-model="clientToEdit.location"
                                :disabled="clientToEdit.connected">
                            <option v-for="location in getAllLocations" :value="location" :key="location.id">
                                {{ location.locationName }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="col">
                    <div class="box">
                        <h4>Smartwatch instellingen</h4>
                        <switch-component
                                :checked="clientToEdit.emergencyEnabled"
                                label="Paniek knop"
                                @click.native="onPanicEnabledClicked">
                        </switch-component>
                    </div>
                </div>

                <div class="col">
                    <div class="box">
                        <h4>Contacten</h4>
                        <div class="add-contacts">
                            <div :key="i" v-for="(contact, i) in clientToEdit.contacts">
                                <p v-if="!clientToEdit.connected" class="deleteButton" @click="removeContact(i)">X</p>
                                <p v-if="!contactIsValid(contact)" class="errorMessage">{{ contactIsInvalidErrorMessage }}</p>
                                <input-component
                                        required="required"
                                        label="Naam"
                                        v-model="contact.contactName"
                                        :disabled="clientToEdit.connected">
                                </input-component>
                                <input-component
                                        required="required"
                                        label="Nummer"
                                        v-model="contact.phoneNumber"
                                        :disabled="clientToEdit.connected">
                                </input-component>
                                <hr>
                            </div>
                            <button
                                    ref="addButton"
                                    class="add-button"
                                    @click="addContact"
                                    v-if="!clientToEdit.connected"
                            >+</button>
                        </div>
                    </div>
                </div>
            </div>
            <button-component
                    @click.native="updatedClient"
                    v-if="!clientToEdit.connected">
                Opslaan
            </button-component>
            <button-component
                    v-if="!clientToEdit.connected"
                    variant="error"
                    @click.native="showModal">
                Verwijderen
            </button-component>
        </form-component>

        <div class="client-created" v-if="clientSaved">
            <div class="complete-image"></div>
            <p class="complete-title">Client {{ this.clientToEdit.initials }} gewijzigd, u wordt teruggestuurd naar het overzicht</p>
            <circle-component position="bottom-center" height=236 />
        </div>

        <Modal v-show="isModalVisible"
               @close="closeModal"
               v-bind:header="modalHeaderText"
               v-bind:body="modalBodyText"
               v-bind:buttonLeft="modalButtonLeft"
               v-bind:buttonRight="modalButtonRight"
               @onDeleteClicked="deleteClient"
        >
        </Modal>
    </div>
</template>

<script>
import { RouteName } from '../../utils/enums';
import ButtonComponent from '../../components/button.component.vue';
import FormComponent from '../../components/fields/form.component.vue';
import InputComponent from '../../components/fields/input.component.vue';
import SwitchComponent from '../../components/fields/switch.component.vue';
import Modal from '../../components/modal.component.vue';
import CircleComponent from '@/components/circle.component.vue';
import { Client } from '../../domain/client';
import { mapGetters } from 'vuex';
import { lastContactIsValid } from '../../utils/validators';

export default {
    name: 'admin-edit-client-page',
    components: {
        FormComponent,
        InputComponent,
        ButtonComponent,
        CircleComponent,
        Modal,
        SwitchComponent
    },
    data() {
        return {
            clientToEdit: Client,
            clientSaved: false,
            clientInitialsErrorMessage: null,
            contactIsInvalidErrorMessage: null,
            clientLocationErrorMessage: null,
            buttonDisabled: false,
            modalHeaderText: 'Cliënt verwijderen',
            modalBodyText: 'Weet u zeker dat u cliënt wilt verwijderen?',
            modalButtonLeft: 'Annuleren',
            modalButtonRight: 'Verwijderen',
            isModalVisible: false,
            color: {
                hue: 50,
                saturation: 100,
                luminosity: 50,
                alpha: 1
            }
        };
    },
    created() {
        this.clientToEdit = this.$store.getters.getClientToEdit;
    },
    computed: {
        ...mapGetters(['getAllLocations']),
    },
    methods: {
        validateForm: function() {
            !this.clientToEdit.initials ? this.clientInitialsErrorMessage = 'Cliëntcode is verplicht' : this.clientInitialsErrorMessage = null;
            !this.clientToEdit.location ? this.clientLocationErrorMessage = 'Woonlocatie is verplicht' : this.clientLocationErrorMessage = null;
            !lastContactIsValid(this.clientToEdit.contacts) ? this.contactIsInvalidErrorMessage = 'Contact is niet juist ingevoerd' : this.contactIsInvalidErrorMessage = null;
            return !this.clientInitialsErrorMessage && !this.contactIsInvalidErrorMessage && !this.clientLocationErrorMessage;
        },
        addContact: function() {
            if (lastContactIsValid(this.clientToEdit.contacts)) {
                this.clientToEdit.contacts.push({
                    contactName: '',
                    phoneNumber: ''
                });
            }
            // Scroll a bit down so the user can add multiple contacts quickly
            this.$refs.addButton.scrollIntoView();
        },
        contactIsValid: function(contact) {
            return contact.contactName.length > 0 && contact.phoneNumber.length >= 3;
        },
        removeContact(index) {
            this.clientToEdit.contacts.splice(index, 1);
        },
        updatedClient() {
            if (this.clientToEdit.location === '') this.clientToEdit.location = null;
            if (this.validateForm() && !this.buttonDisabled) {
                this.buttonDisabled = true; // disable after first click, to prevent multiple clients are saved
                this.$store.dispatch('UPDATE_CLIENT', this.clientToEdit)
                    .then(response => {
                        if (response.status === 200) { // updated, show confirmation
                            this.clientSaved = true; // redirect back after 2 seconds
                            setTimeout(() => {
                                this.$router.push({name: RouteName.ADMIN_CLIENTS});
                            }, 2500);
                        }
                    })
                    .catch(error => {
                        console.error(error);
                    })
            } else {
                console.error('Niet alle velen zijn juist ingevoerd');
            }
        },
        deleteClient: function () {
            this.closeModal();
            this.$store.dispatch('DELETE_CLIENT', this.clientToEdit.id)
                .then(response => {
                if (response.status === 200) {
                    this.$router.push({ name: RouteName.ADMIN_CLIENTS });
                }
                else {
                    console.error(response);
                }
            });
        },
        showModal() {
            this.isModalVisible = true;
        },
        closeModal() {
            this.isModalVisible = false;
        },
        onPanicEnabledClicked: function() {
            this.clientToEdit.emergencyEnabled = !this.clientToEdit.emergencyEnabled;
        }
    }
};
</script>

<style scoped lang="scss">
    @import '~@radial-color-picker/vue-color-picker/dist/vue-color-picker.min.css';
    .row {
        flex-direction: column;

        @include desktop {
            flex-direction: row;
        }
    }

    .box {
        margin-bottom: 2rem;

        @include desktop {
            background-color: $white;
            // height: 100%;
            padding: 1rem;
            box-shadow: 0 -2px 8px 0px rgba(0, 0, 0, 0.1);
            border-radius: 0.25rem;
        }

        h4 {
            font-weight: 400;
            margin-bottom: 1rem;
            color: $gray-darkest;
        }
        .errorMessage {
            color: $error;
            font-size: 12px;
            font-weight: normal;
        }

        .deleteButton {
            color: red;
            font-weight: bold;
            margin-bottom: 0px;

            &:hover {
                cursor: pointer;
            }
        }
    }

    .add-contacts {
        border-radius: 4px;
        border: 1px solid $gray;
        padding: 1rem;
        position: relative;
        margin-bottom: 1rem;

        .add-button {
            $button-size: 42px;
            position: absolute;
            bottom: -($button-size / 2);
            left: 50%;
            margin-left: -($button-size / 2);
            width: $button-size;
            height: $button-size;
            border-radius: 50%;
            background-color: $primary;
            color: $white;
            font-weight: bold;

            &:hover {
                background-color: $lightGreen;
            }
        }
    }

    .subtitle {
        margin: 1rem 0;
        display: block;
        font-weight: 500;
    }

    .seperator {
        margin: 2rem 0;
        background-color: $gray;
    }

    .input-component,
    .switch-component {
        margin-bottom: 1rem;
    }

    .row-wrapper {
        display: flex;

        .input-component {
            flex-grow: 1;

            &:first-child {
                margin-right: 60px;
            }
        }
    }

    .smartwatch-bg-color {
        margin-bottom: 1rem;
    }
    .client-created {
        height: 100%;
        background-color: $primary;
        color: $white;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .complete-title {
            margin-bottom: 14px;
        }

        p {
            font-size: 16px;
            text-align: center;
        }
    }

    .complete-image {
        width: 95px;
        height: 120px;
        background-image: url('../../assets/icons/watch-large-complete.svg');
        margin-bottom: 64px;
        margin-top: -60px;
    }
    select {
        width: 100%;
        border: 1px solid $gray;
        border-radius: 3px;
        height: 52px;
        padding: 1rem;

        &:hover {
            cursor: pointer;
        }
    }
</style>
